import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { api } from '../api';
import type { Hotbar } from '../api/types';
import { ehMagia, ehMagiaPronta, HOTBAR_SIZE, HOTBAR_VAZIA, MAGIAS, cicloMs } from '../data/magias';
import { ehArmaCombate } from '../data/armas';
import { ehConsumivel, ehGratis, ehPocaoVip, esperaConsumivelMs, grupoConsumivel } from '../data/consumiveis';
import { paraBarra, comArmaEquipada, colocarNaBarra, alvoDoClique, moverNaBarra, SLOT_ARMA, acaoDoSlot, tipoDaEspera, type EsperaKind } from '../data/hotbar';
import { t } from '../i18n/idioma';
import { nomeDaMagia, nomeDoItem } from '../i18n/nomes';
import { ACOES_DA_BARRA, ligarAcao, rotuloTecla, teclaDe } from '../input/atalhos';
import { useFechar } from '../input/useAtalhos';
import type { CastPulse, RecargaPulse, WorldStatus } from '../world/types';
import { SkillSlot } from './SkillSlot';
import { SpellPicker } from './SpellPicker';
import { CombateIdleConfig } from './CombateIdleConfig';
import type { FaltaBarra } from './hud/DicasHud';
import { GumpWindow } from './GumpWindow';
import { cursorArraste } from '../input/cursor';
import { IcPicareta, IcTaticas, IcViajar } from './icones/IconesV3';
import { PainelEfeitos, PlacaAlvo, PlacaVida } from './hud/BarrasVitais';
import { IconeCorrida } from './hud/IconeCorrida';
import { aplicarEscalaBarra, aplicarFundoBarra, EVENTO_BARRA, lerBarra, lerFundoBarra, linhasDaBarra, type PrefBarra } from './hud/preferencias';
import { BotoesFlutuantes, ancoraAoLado, type PosicoesBotoes } from './hud/BotoesFlutuantes';
import { CartaFechada } from './correio/ArteCorreio';
import { IconeHud } from './hud/icones';
import { useModo } from './hud/useModoTela';
import { DicaHost } from './hud/Dica';
import { definirSkillsMagia } from '../world/skillsMagia';

/** Magia e Avaliar Int. do perfil, para as caixas de dica das magias (techspec-tooltips-e-efeitos). */
function guardarSkillsMagia(skills: readonly { id: string; value: number }[]) {
  const de = (id: string) => skills.find(s => s.id === id)?.value ?? 0;
  definirSkillsMagia(de('magery'), de('evalInt'));
}

/** O nome do que está no slot, para a pergunta de limpar. */
function nomeNoSlot(id: string | null | undefined): string {
  if (!id) return '';
  return ehMagia(id) ? nomeDaMagia(id) : nomeDoItem(id);
}

const SAVE_MS = 400;

/** A recarga que vale para o slot: a da magia ou a global, a que acaba depois. */
function recargaDoSlot(propria: { ate: number; ms: number } | undefined, global: { ate: number; ms: number } | null) {
  const agora = performance.now();
  const vivas = [propria, global ?? undefined].filter((e): e is { ate: number; ms: number } => !!e && e.ate > agora);
  if (vivas.length === 0) return null;
  return vivas.reduce((a, b) => (b.ate > a.ate ? b : a));
}

type Espera = { index: number; ate: number; ms: number; tipo: EsperaKind };
type EsperaUso = { ate: number; ms: number };

interface SkillBarProps {
  status: WorldStatus | null;
  /** Personagem já entrou no mundo — aí o GET da barra tem dono. */
  armed: boolean;
  onWarn(text: string): void;
  /** Atalhos da barra. Espaço no slot da arma. Devolve true se saiu. */
  onCastSlot?(index: number, ignoraAlvo?: boolean, idSlot?: string | null): boolean;
  /** Recusa de magia/golpe. Não apaga o CD das poções. */
  castRefuse?: number;
  /** Uso de poção/bandagem abortado (cheia, vazia, sem veneno). Espera compartilhada não dispara. */
  consumeRefuse?: number;
  /** A IA usou poção/bandagem: recarga no slot que tem o mesmo grupo. */
  consumePulse?: { seq: number; id: string; ms: number } | null;
  /** A conjuração pelo servidor: a sombra do slot dura o que o servidor mandou; `ms` 0 apaga. */
  castPulse?: CastPulse | null;
  /** A recarga de uma magia pelo servidor ("cd"; techspec-combate-v2): o CD dela e o global. */
  recargaPulse?: RecargaPulse | null;
  /** Swing aceito — a sombra dura o intervalo do próximo golpe. */
  swingPulse?: { seq: number; ms: number } | null;
  /** Arma no mainHand. `undefined` = ainda não chegou; não esvazia o slot 0. */
  mainHandDefId?: string | null;
  /** Recarrega o fator de magia quando o equipamento muda. */
  gearTick?: number;
  /** Contagem na mochila dos consumíveis da barra. */
  consumiveis?: Readonly<Record<string, number>>;
  /** War de arma: borda vermelha no slot e o botão Guerra. */
  war?: boolean;
  /** O retrato da placa do alvo (o boneco do jogador ou o busto do monstro), pelo ShortId. */
  retratoDoAlvo?: (canvas: HTMLCanvasElement, id: number) => () => void;
  /** Duelo v2: o adversário (a placa do alvo ganha a borda do duelo e a sua placa o contorno dourado). */
  adversarioDuelo?: string | null;
  /** Modo de verdade da caçada — o rótulo do botão de combate. */
  huntManual?: boolean;
  packOpen?: boolean;
  onTogglePack?(): void;
  vip?: { ativo: boolean; prata: number; semBanco: boolean; precos: Record<string, number> } | null;
  /** Passo da barra: a janela fica aberta e o jogador só mexe no que o tutorial pede. */
  tutorialBarra?: boolean;
  tutorialAtaque?: string | null;
  onTutorialRestam?(falta: FaltaBarra): void;
  /** Em caçada: o botão de combate mostra AUTO ou MANUAL. */
  cacando?: boolean;
  /** O botão flutuante de Viajar (a janela é do WorldScreen). */
  viajarAberto?: boolean;
  onViajar?(): void;
  recursosAberto?: boolean;
  onRecursos?(): void;
  /** Onde os botões flutuantes estão: Viajar abre ao lado do seu. */
  onPosicoesFab?(p: PosicoesBotoes): void;
  /** O botão flutuante de Amigos (techspec-hud-v3 §23); a janela é do WorldScreen. */
  amigosAberto?: boolean;
  amigosNaoLidas?: number;
  onAmigos?(): void;
  /** A carta flutuante do correio (techspec-correio §06): só com carta nova; a janela é do WorldScreen. */
  correio?: { novas: number; chegou: boolean; titulo?: string } | null;
  onCorreio?(): void;
}

/** Vida, mana e vigor: uma linha, o número dentro, os entalhes da cura automática. */

export function SkillBar({ status, armed, onWarn, onCastSlot, castRefuse, consumeRefuse, consumePulse, castPulse, recargaPulse, swingPulse, mainHandDefId, gearTick, consumiveis, war, adversarioDuelo, retratoDoAlvo, huntManual, packOpen, onTogglePack, vip, tutorialBarra, tutorialAtaque, onTutorialRestam, cacando, viajarAberto, onViajar, recursosAberto, onRecursos, onPosicoesFab, amigosAberto, amigosNaoLidas = 0, onAmigos, correio, onCorreio }: SkillBarProps) {
  const modo = useModo();
  // A transparência do fundo da barra (Configuração › Interface): aplica o que o navegador guardou.
  useEffect(() => { aplicarFundoBarra(lerFundoBarra()); }, []);
  // Tamanho, slots e linhas da barra (Configuração › Interface, 28/10/2026): relê quando o jogador mexe.
  const [prefBarra, setPrefBarra] = useState<PrefBarra>(lerBarra);
  useEffect(() => {
    aplicarEscalaBarra(prefBarra.escala);
    const ouvir = (e: Event) => setPrefBarra((e as CustomEvent<PrefBarra>).detail ?? lerBarra());
    window.addEventListener(EVENTO_BARRA, ouvir);
    return () => window.removeEventListener(EVENTO_BARRA, ouvir);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const slotsVisiveis = modo === 'cel' ? 6 : prefBarra.slots;
  const [posFab, setPosFab] = useState<PosicoesBotoes>({});
  const [novoSlot, setNovoSlot] = useState<number | null>(null);
  const [slots, setSlots] = useState<(string | null)[]>(() => [...HOTBAR_VAZIA]);
  const [ready, setReady] = useState(false);
  const [picked, setPicked] = useState<number | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [idleOpen, setIdleOpen] = useState(false);
  useEffect(() => { setIdleOpen(!!tutorialBarra); }, [tutorialBarra]);
  const [faltaCombate, setFaltaCombate] = useState({ cura: false, mana: false, ataque: true });
  useEffect(() => {
    if (!tutorialBarra) return;
    onTutorialRestam?.({
      cura: false,
      mana: false,
      ataque: faltaCombate.ataque,
      f1: false,
      f2: false
    });
  }, [tutorialBarra, faltaCombate.ataque, onTutorialRestam]);
  const [magery, setMagery] = useState<number | null>(null);
  const [velMagia, setVelMagia] = useState(0);
  const [espera, setEspera] = useState<Espera | null>(null);
  const [esperasUso, setEsperasUso] = useState<Readonly<Record<string, EsperaUso>>>({});
  const esperasUsoRef = useRef(esperasUso);
  esperasUsoRef.current = esperasUso;
  const esperaRef = useRef(espera);
  esperaRef.current = espera;
  /** A recarga das magias (techspec-combate-v2): por magia e a global, que trava todas. */
  const [recargas, setRecargas] = useState<Readonly<Record<string, EsperaUso>>>({});
  const [recargaGlobal, setRecargaGlobal] = useState<EsperaUso | null>(null);
  const recargasRef = useRef(recargas);
  recargasRef.current = recargas;
  const recargaGlobalRef = useRef(recargaGlobal);
  recargaGlobalRef.current = recargaGlobal;
  /** O modo "respeitar a magia em curso" vale para a conjuração de agora (vem no "cs"). */
  const travaCast = useRef(false);
  const pendingUso = useRef<string | null>(null);
  const saveTimer = useRef<number>(0);
  /** Slot que recebe o foco quando o seletor fecha (D13). */
  const focoSlot = useRef<number | null>(null);
  const arraste = useRef<{ de: number; x: number; y: number; id: number; movendo: boolean } | null>(null);
  const pulouClique = useRef(false);
  /** O slot que o botão direito quer limpar: a barra pergunta antes. */
  const [limpar, setLimpar] = useState<number | null>(null);
  useFechar(() => setLimpar(null), { ativo: limpar !== null, deixaPassar: ACOES_DA_BARRA });
  useEffect(() => {
    if (limpar === null) return;
    const fora = (ev: PointerEvent) => {
      if (!(ev.target as HTMLElement | null)?.closest?.('.bb-limpar')) setLimpar(null);
    };
    window.addEventListener('pointerdown', fora);
    return () => window.removeEventListener('pointerdown', fora);
  }, [limpar]);
  const [arrastando, setArrastando] = useState<number | null>(null);
  const [soltarEm, setSoltarEm] = useState<number | null>(null);
  const mainHandRef = useRef(mainHandDefId);
  mainHandRef.current = mainHandDefId;
  const warn = useRef(onWarn);
  warn.current = onWarn;

  useEffect(() => {
    if (!armed) return;
    let live = true;
    let tries = 0;
    let retry = 0;
    const load = () => {
      api.getHotbar()
        .then(bar => {
          if (!live) return;
          const cru = pad(bar);
          const arrumada = comArmaEquipada(cru, mainHandRef.current);
          setSlots(arrumada);
          setReady(true);
        })
        .catch(() => {
          if (!live) return;
          tries += 1;
          if (tries < 8) {
            retry = window.setTimeout(load, 350);
            return;
          }
          setSlots([...HOTBAR_VAZIA]);
          setReady(false);
          warn.current(t('magiaUi.naoCarregou'));
        });
    };
    load();
    return () => {
      live = false;
      window.clearTimeout(retry);
    };
  }, [armed]);

  useEffect(() => {
    if (!armed) return;
    let live = true;
    api.getProfile()
      .then(sheet => {
        if (!live) return;
        setVelMagia((sheet.combat.velMagiaBp ?? 0) / 10_000);
        guardarSkillsMagia(sheet.skills);
      })
      .catch(() => { if (live) setVelMagia(0); });
    return () => { live = false; };
  }, [armed, gearTick]);

  useEffect(() => {
    if (open === null) {
      setMagery(null);
      return;
    }
    let live = true;
    api.getProfile()
      .then(sheet => {
        if (!live) return;
        const skill = sheet.skills.find(s => s.id === 'magery');
        setMagery(skill?.value ?? 0);
        guardarSkillsMagia(sheet.skills);
      })
      .catch(() => { if (live) setMagery(null); });
    return () => { live = false; };
  }, [open]);

  const persist = useCallback((next: readonly (string | null)[]) => {
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      void api.setHotbar(paraBarra(next) as Hotbar).catch(() => {
        warn.current(t('magiaUi.naoGravou'));
      });
    }, SAVE_MS);
  }, []);

  useEffect(() => () => window.clearTimeout(saveTimer.current), []);

  const aplicar = useCallback((prev: readonly (string | null)[], rascunho: readonly (string | null)[], gravar = true): (string | null)[] => {
    const next = comArmaEquipada(rascunho, mainHandDefId);
    if (next.length === prev.length && next.every((s, i) => s === prev[i])) return prev as (string | null)[];
    if (gravar) persist(next);
    return next;
  }, [mainHandDefId, persist]);

  useEffect(() => {
    if (!ready) return;
    setSlots(prev => aplicar(prev, prev, false));
  }, [aplicar, ready]);

  const place = useCallback((index: number, id: string | null, focar = false) => {
    setSlots(prev => {
      const { next, fim } = colocarNaBarra(prev, index, id, mainHandDefId);
      if (focar) focoSlot.current = fim >= 0 ? fim : index;   // idempotente: o StrictMode roda o updater duas vezes
      return aplicar(prev, next);
    });
    if (id) setNovoSlot(index);
    setOpen(null);
  }, [aplicar, mainHandDefId]);

  const mover = useCallback((de: number, para: number) => {
    if (tutorialBarra) return;
    setSlots(prev => aplicar(prev, moverNaBarra(prev, de, para, mainHandDefId)));
  }, [aplicar, mainHandDefId, tutorialBarra]);

  const slotNoPonto = (x: number, y: number): number | null => {
    const el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-hotbar-slot]');
    if (!el) return null;
    const n = Number(el.dataset.hotbarSlot);
    if (!Number.isInteger(n) || n <= SLOT_ARMA || n > slotsVisiveis) return null;
    return n;
  };

  const iniciarArraste = (e: ReactPointerEvent, index: number) => {
    if (e.button !== 0 || !ready || tutorialBarra || index === SLOT_ARMA || !slots[index]) return;
    arraste.current = { de: index, x: e.clientX, y: e.clientY, id: e.pointerId, movendo: false };
  };

  const acompanharArraste = (e: ReactPointerEvent) => {
    const a = arraste.current;
    if (!a || e.pointerId !== a.id) return;
    if (!a.movendo && Math.hypot(e.clientX - a.x, e.clientY - a.y) >= 6) {
      a.movendo = true;
      setArrastando(a.de);
      cursorArraste('barra', true);
      /* A captura só quando o arrasto começa: capturar já no toque fazia o clique cair na moldura do slot e não no
         botão, e clicar num slot com poção ou magia não usava nada. */
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* ponteiro já solto */ }
    }
    if (!a.movendo) return;
    const destino = slotNoPonto(e.clientX, e.clientY);
    setSoltarEm(destino != null && destino !== a.de ? destino : null);
  };

  const terminarArraste = (e: ReactPointerEvent) => {
    const a = arraste.current;
    if (!a || e.pointerId !== a.id) return;
    arraste.current = null;
    const destino = a.movendo ? slotNoPonto(e.clientX, e.clientY) : null;
    setArrastando(null);
    setSoltarEm(null);
    cursorArraste('barra', false);
    if (a.movendo) {
      pulouClique.current = true;
      window.setTimeout(() => { pulouClique.current = false; }, 0);
    }
    if (destino != null && destino !== a.de) mover(a.de, destino);
  };

  const closePicker = useCallback(() => setOpen(o => {
    if (o !== null) focoSlot.current = o;
    return null;
  }), []);

  useEffect(() => {
    if (open !== null) return;
    const i = focoSlot.current;
    if (i == null) return;
    focoSlot.current = null;
    document.querySelector<HTMLElement>(`.skillbar [data-slot="${i}"]`)?.focus({ preventScroll: true });
  }, [open, slots]);
  useFechar(closePicker, {
    ativo: open !== null,
    modal: true,
    deixaPassar: ACOES_DA_BARRA
  });

  const disparar = useCallback((index: number) => {
    if (tutorialBarra) return;
    setPicked(index);
    const id = slots[index] ?? null;
    if (id && ehConsumivel(id)) {
      const grupo = grupoConsumivel(id);
      if (!grupo) return;
      const atual = esperasUsoRef.current[grupo];
      if (atual && atual.ate > performance.now()) return;
      const ms = esperaConsumivelMs(id);
      pendingUso.current = grupo;
      setEsperasUso(cur => ({ ...cur, [grupo]: { ate: performance.now() + ms, ms } }));
      window.setTimeout(() => {
        if (pendingUso.current === grupo) pendingUso.current = null;
      }, 800);
      onCastSlot?.(index, true);
      return;
    }
    if (id && ehMagia(id)) {
      // Em recarga (a dela ou a global): o clique não sai. O servidor recusa do mesmo jeito; aqui só poupa o aviso.
      const agora = performance.now();
      if ((recargasRef.current[id]?.ate ?? 0) > agora || (recargaGlobalRef.current?.ate ?? 0) > agora) return;
      const cur = esperaRef.current;
      if (cur && cur.tipo === 'conjuracao' && cur.ate > performance.now()) {
        const emCurso = slots[cur.index];
        if (emCurso === id) return;
        // Modo "respeitar a magia em curso": outra magia não cancela a que está no ar (o servidor também ignora).
        if (travaCast.current) return;
      }
    }
    // Cura e buff não pedem alvo: sem jogador marcado, o buff cai em você (techspec-magias-de-efeito).
    const ignora = !!id && ehMagia(id) && (MAGIAS[id].kind === 'heal' || MAGIAS[id].kind === 'buff' || MAGIAS[id].kind === 'resurrect');
    onCastSlot?.(index, ignora, id);
  }, [onCastSlot, slots, tutorialBarra]);

  useEffect(() => {
    const offs: Array<() => void> = [];
    for (let i = 1; i <= slotsVisiveis; i++) {
      const index = i;
      offs.push(ligarAcao(acaoDoSlot(index), () => disparar(index)));
    }
    return () => { for (const off of offs) off(); };
  }, [disparar, slotsVisiveis]);

  useEffect(() => {
    if (castRefuse == null || castRefuse === 0) return;
    // A recarga da magia é do "cs" do servidor: uma recusa (de outra magia) não apaga a que está no ar.
    setEspera(cur => (cur?.tipo === 'conjuracao' ? cur : null));
  }, [castRefuse]);

  useEffect(() => {
    if (consumeRefuse == null || consumeRefuse === 0) return;
    const grupo = pendingUso.current;
    pendingUso.current = null;
    if (!grupo) return;
    setEsperasUso(cur => {
      if (!(grupo in cur)) return cur;
      const next = { ...cur };
      delete next[grupo];
      return next;
    });
  }, [consumeRefuse]);

  useEffect(() => {
    if (!consumePulse?.id) return;
    const grupo = grupoConsumivel(consumePulse.id);
    if (!grupo) return;
    const ms = consumePulse.ms > 0 ? consumePulse.ms : esperaConsumivelMs(consumePulse.id);
    setEsperasUso(cur => ({ ...cur, [grupo]: { ate: performance.now() + ms, ms } }));
  }, [consumePulse]);

  useEffect(() => {
    if (!castPulse) return;
    if (castPulse.ms <= 0) {
      travaCast.current = false;
      setEspera(cur => (cur?.tipo === 'conjuracao' ? null : cur));
      return;
    }
    travaCast.current = castPulse.trava;
    const index = slots.findIndex(s => s === castPulse.id);
    if (index < 0 || !ehMagia(castPulse.id)) return;
    // A duração vem do servidor (CombatMath.CastTicks com a velocidade de magia de agora): a sombra termina junto
    // com o acerto ou a falha. O cicloMs local fica só de reserva, para um servidor antigo.
    const ms = castPulse.ms > 0 ? castPulse.ms : cicloMs(castPulse.id, velMagia);
    setEspera({ index, ate: performance.now() + ms, ms, tipo: 'conjuracao' });
  }, [castPulse, velMagia]);

  useEffect(() => {
    if (!recargaPulse) return;
    const agora = performance.now();
    if (!recargaPulse.id && recargaPulse.ms <= 0 && recargaPulse.globalMs <= 0) {
      setRecargas({});
      setRecargaGlobal(null);
      return;
    }
    if (recargaPulse.id && recargaPulse.ms > 0)
      setRecargas(cur => ({ ...cur, [recargaPulse.id]: { ate: agora + recargaPulse.ms, ms: recargaPulse.ms } }));
    if (recargaPulse.globalMs > 0) setRecargaGlobal({ ate: agora + recargaPulse.globalMs, ms: recargaPulse.globalMs });
  }, [recargaPulse]);

  // Some a recarga que acabou (um só relógio, no fim da mais próxima).
  useEffect(() => {
    const agora = performance.now();
    const fins = [...Object.values(recargas), ...(recargaGlobal ? [recargaGlobal] : [])].map(e => e.ate - agora);
    if (fins.length === 0) return;
    const t = window.setTimeout(() => {
      const now = performance.now();
      setRecargas(cur => {
        const next: Record<string, EsperaUso> = {};
        for (const [id, e] of Object.entries(cur)) if (e.ate > now) next[id] = e;
        return Object.keys(next).length === Object.keys(cur).length ? cur : next;
      });
      setRecargaGlobal(cur => (cur && cur.ate > now ? cur : null));
    }, Math.max(0, Math.min(...fins)) + 20);
    return () => window.clearTimeout(t);
  }, [recargas, recargaGlobal]);

  useEffect(() => {
    if (!swingPulse) return;
    const index = picked != null && ehArmaCombate(slots[picked])
      ? picked
      : slots.findIndex(s => s != null && ehArmaCombate(s));
    if (index < 0) return;
    setEspera({ index, ate: performance.now() + swingPulse.ms, ms: swingPulse.ms, tipo: 'golpe' });
  }, [swingPulse, picked, slots]);

  useEffect(() => {
    if (!espera) return;
    const wait = Math.max(0, espera.ate - performance.now());
    const t = window.setTimeout(() => setEspera(null), wait);
    return () => window.clearTimeout(t);
  }, [espera?.ate]);

  useEffect(() => {
    const agora = performance.now();
    const proximas = Object.values(esperasUso)
      .map(e => e.ate - agora)
      .filter(ms => ms > 0);
    if (proximas.length === 0) {
      if (Object.keys(esperasUso).length > 0) setEsperasUso({});
      return;
    }
    const t = window.setTimeout(() => {
      const now = performance.now();
      setEsperasUso(cur => {
        const next: Record<string, EsperaUso> = {};
        for (const [g, e] of Object.entries(cur)) {
          if (e.ate > now) next[g] = e;
        }
        return next;
      });
    }, Math.min(...proximas));
    return () => window.clearTimeout(t);
  }, [esperasUso]);

  const slotDe = (i: number) => {
    const emGuerra = !!war && i === SLOT_ARMA;
    const idSlot = slots[i];
    const grupo = grupoConsumivel(idSlot);
    const uso = grupo ? esperasUso[grupo] : undefined;
    const magiaOuGolpe = !!espera && espera.tipo !== 'uso' && espera.index === i;
    // A recarga da magia (a dela ou a global, a que acaba depois) aparece quando a conjuração não está no slot.
    const recarga = !magiaOuGolpe && idSlot && ehMagia(idSlot) ? recargaDoSlot(recargas[idSlot], recargaGlobal) : null;
    const visual = uso ?? (magiaOuGolpe ? espera : recarga);
    return (
      <div
        key={i}
        data-hotbar-slot={i}
        className={`bb-w${soltarEm === i ? ' is-soltar' : ''}`}
        onPointerDown={e => iniciarArraste(e, i)}
        onPointerMove={acompanharArraste}
        onPointerUp={terminarArraste}
        onPointerCancel={terminarArraste}
        onClickCapture={e => {
          if (!pulouClique.current) return;
          pulouClique.current = false;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {emGuerra && (
          <button
            type="button"
            className="skillbar__guerra"
            aria-label={t('atalho.acao.paz')}
            onClick={() => disparar(i)}
          >
            <svg className="skillbar__guerra-x" viewBox="0 0 16 16" aria-hidden="true">
              <path fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" d="M2.5 13.5 10 6M13.5 2.5 10 6M2.5 2.5 10 10M13.5 13.5 10 10" />
            </svg>
            {t('mundo.guerra')}
          </button>
        )}
        <SkillSlot
          index={i}
          spellId={idSlot ?? null}
          selected={open === i}
          war={emGuerra}
          disabled={!ready}
          esperaAte={visual ? visual.ate : null}
          esperaMs={visual ? visual.ms : 0}
          esperaTipo={uso ? 'uso' : magiaOuGolpe ? espera!.tipo : recarga ? 'recarga' : tipoDaEspera(idSlot)}
          manaAtual={status == null ? null : status.mana}
          velMagia={velMagia}
          onOpen={() => {
            if (!ready || i === SLOT_ARMA) return;
            if (tutorialBarra) return;
            setIdleOpen(false);
            setOpen(alvoDoClique(slots, i));
          }}
          onClear={() => {
            if (i === SLOT_ARMA || tutorialBarra) return;
            // O botão direito pergunta antes de tirar (techspec-hud-v3): um clique errado não some com a magia.
            setLimpar(i);
          }}
          arrastando={arrastando === i}
          confirmarLimpar={limpar === i ? (
            <div className="bb-limpar" role="alertdialog" aria-label={t('magiaUi.limparTitulo')}
              onPointerDown={e => e.stopPropagation()} onClick={e => e.stopPropagation()} onContextMenu={e => { e.preventDefault(); e.stopPropagation(); }}>
              <p>{t('magiaUi.limparPergunta', { nome: nomeNoSlot(idSlot) })}</p>
              <div className="bb-limpar__acoes">
                <button type="button" className="btn btn--tiny" autoFocus onClick={() => setLimpar(null)}>{t('comum.cancelar')}</button>
                <button type="button" className="btn btn--tiny btn--danger" onClick={() => { setLimpar(null); place(i, null); }}>{t('comum.remover')}</button>
              </div>
            </div>
          ) : null}
          onUse={() => { setNovoSlot(null); disparar(i); }}
          novo={novoSlot === i}
          qty={idSlot && ehConsumivel(idSlot) && !ehGratis(idSlot) ? (consumiveis?.[idSlot] ?? 0) : undefined}
          vip={!!vip?.ativo && ehPocaoVip(idSlot)}
          semBanco={!!vip?.semBanco}
          preco={idSlot ? vip?.precos[idSlot] : undefined}
          prata={vip?.prata}
        />
      </div>
    );
  };

  /* De 8 a 16 slots, numa fileira ou em duas (metade em cada), Configuração › Interface; no celular, F1–F6 */
  const grupos = modo === 'cel' ? [[1, 2, 3, 4, 5, 6]] : linhasDaBarra(prefBarra.slots, prefBarra.duasLinhas);
  const duasLinhas = modo !== 'cel' && grupos.length > 1;
  const posicoes = (p: PosicoesBotoes) => { setPosFab(p); onPosicoesFab?.(p); };

  return (
    <>
      {/* Vida, mana e vigor saíram da barra (techspec-hud-v3 §19): a placa mora na grade dos botões flutuantes (§23),
          e a do alvo também, só em combate (§25). */}
      <DicaHost />
      <PainelEfeitos />
      <div className="skillbar bb" role="toolbar" aria-label={t('magiaUi.barra')}>
        <div className={`bb-sl${duasLinhas ? ' bb-sl--2' : ''}`}>
          <IconeCorrida stam={status?.stam} stamMax={status?.stamMax} />
          {slotDe(SLOT_ARMA)}
          {duasLinhas
            ? <span className="bb-linhas">{grupos.map(g => <span className="bb-g" key={g[0]}>{g.map(slotDe)}</span>)}</span>
            : grupos.map(g => <span className="bb-g" key={g[0]}>{g.map(slotDe)}</span>)}
        </div>
      </div>
      <BotoesFlutuantes
        onPosicoes={posicoes}
        botoes={[
          {
            id: 'bolsa',
            rotulo: t('mochila.titulo'),
            legenda: t('mochila.titulo'),
            dica: t('hud.fab.mochila', { tecla: rotuloTecla(teclaDe('mochila')) }),
            ligado: !!packOpen,
            conteudo: <img src="/sprites/itens/hud/bag.png" alt="" draggable={false} />,
            onClick: () => { setOpen(null); setIdleOpen(false); onTogglePack?.(); }
          },
          {
            id: 'combate',
            rotulo: t('combateIdle.titulo'),
            legenda: t('hud.fab.legCombate'),
            dica: t('hud.fab.combate'),
            ligado: idleOpen,
            conteudo: (
              <>
                <IcTaticas />
                {cacando && (
                  <span className={`v3-fab__chip${huntManual ? ' v3-fab__chip--manual' : ''}`}>
                    {huntManual ? t('hud.fab.manual') : t('hud.fab.auto')}
                  </span>
                )}
              </>
            ),
            onClick: () => { setOpen(null); if (tutorialBarra) return; setIdleOpen(x => !x); }
          },
          {
            id: 'viajar',
            rotulo: t('hud.menu.viajar'),
            legenda: t('hud.menu.viajar'),
            dica: t('hud.fab.viajar', { tecla: rotuloTecla(teclaDe('viajar')) }),
            ligado: !!viajarAberto,
            conteudo: <IcViajar />,
            onClick: () => { setOpen(null); onViajar?.(); }
          },
          {
            id: 'recursos',
            rotulo: t('hud.viajar.recursos'),
            legenda: t('hud.viajar.recursos'),
            dica: t('hud.fab.recursos'),
            ligado: !!recursosAberto,
            conteudo: <IcPicareta />,
            onClick: () => { setOpen(null); onRecursos?.(); }
          },
          {
            id: 'amigos',
            rotulo: t('amigo.titulo'),
            legenda: t('amigo.titulo'),
            dica: t('hud.fab.amigos', { tecla: rotuloTecla(teclaDe('amigos')) }),
            ligado: !!amigosAberto,
            conteudo: <IconeHud nome="amigos" className="ch-ic v3-fab__gente" />,
            pip: amigosNaoLidas,
            onClick: () => { setOpen(null); onAmigos?.(); }
          },
          ...(correio && correio.novas > 0 ? [{
            id: 'correio' as const,
            rotulo: t(correio.novas > 1 ? 'correio.flut.rotuloN' : 'correio.flut.rotulo1', { n: correio.novas }),
            legenda: t('correio.titulo'),
            dica: correio.titulo || t('correio.titulo'),
            classe: `co-fab${correio.chegou ? ' is-chegou' : ''}`,
            conteudo: (
              <>
                <span className="co-fab__luz" aria-hidden="true" />
                <span className="co-fab__carta"><CartaFechada /></span>
              </>
            ),
            pip: correio.novas,
            onClick: () => { setOpen(null); onCorreio?.(); }
          }] : []),
          {
            id: 'vida',
            rotulo: t('hud.vitais.titulo'),
            colunas: 2,
            classe: adversarioDuelo ? 'is-duelo-eu' : undefined,
            conteudo: <PlacaVida status={status} />
          },
          ...(status?.alvo ? [{
            id: 'alvo' as const,
            rotulo: t('hud.vitais.alvo', { nome: status.alvo.nome }),
            colunas: retratoDoAlvo ? 3 : 2,
            classe: `v3-fab__placa--alvo is-${status.alvo.kind}${adversarioDuelo && status.alvo.nome.toLowerCase() === adversarioDuelo.toLowerCase() ? ' is-duelo' : ''}`,
            conteudo: <PlacaAlvo alvo={status.alvo} retratoDe={retratoDoAlvo} />
          }] : [])
        ]}
      />
      {idleOpen && (
        <GumpWindow
          title={t('combateIdle.titulo')}
          anchor={ancoraAoLado(posFab, 'combate')}
          className="pop cb win--combate"
          comeSlots
          onClose={() => { if (!tutorialBarra) setIdleOpen(false); }}
        >
          <CombateIdleConfig
            mainHandDefId={mainHandDefId}
            vip={!!vip?.ativo}
            tutorialAtaque={tutorialBarra ? tutorialAtaque : null}
            onTutorialRestam={setFaltaCombate}
          />
        </GumpWindow>
      )}
      {open !== null && (
        <>
          <button type="button" className="picker-back" aria-label={t('magiaUi.fecharGrimorio')} onClick={closePicker} />
          <SpellPicker
            slot={open}
            barra={slots}
            magery={magery}
            velMagia={velMagia}
            consumiveis={consumiveis}
            vip={!!vip?.ativo}
            soItem={null}
            onLimpar={() => { if (open !== SLOT_ARMA) place(open, null); }}
            onPick={id => {
              if (open === SLOT_ARMA) return;
              if (ehConsumivel(id)) {
                place(open, id, true);
                return;
              }
              if (!ehMagia(id) || !ehMagiaPronta(id)) return;
              place(open, id, true);
            }}
            onClose={closePicker}
          />
        </>
      )}
    </>
  );
}

function pad(bar: Hotbar): (string | null)[] {
  return Array.from({ length: HOTBAR_SIZE }, (_, i) => bar[i] ?? null);
}
